import { useState } from "react"
import { Moon, Sun } from "lucide-react"
import { type CalendarDisplayEvent } from "@/pages/CalendarPage"
import { useCurrency } from "@/context/currency/currency-context"
import { DayDetailDialog } from "./day-detail-dialog"
import { cn } from "@/lib/utils"

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const STATUS_DOT: Record<string, string> = {
	paid: "bg-green-500",
	declared: "bg-[#0ea5e9]",
	predicted: "bg-sky-400",
	earnings: "bg-indigo-500",
}

interface CalendarGridProps {
	year: number
	month: number // 0-indexed
	eventsByDate: Map<string, CalendarDisplayEvent[]>
}

function toDateKey(year: number, month: number, day: number): string {
	return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

export function CalendarGrid({ year, month, eventsByDate }: CalendarGridProps) {
	const { format, convert } = useCurrency()
	const [selectedDate, setSelectedDate] = useState<Date | null>(null)
	const [dialogOpen, setDialogOpen] = useState(false)

	const today = new Date()
	const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month
	const todayDate = today.getDate()

	const firstDayOfWeek = new Date(year, month, 1).getDay() // 0=Sun
	const daysInMonth = new Date(year, month + 1, 0).getDate()

	const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7

	const handleDayClick = (day: number) => {
		const key = toDateKey(year, month, day)
		const events = eventsByDate.get(key)
		if (events && events.length > 0) {
			setSelectedDate(new Date(year, month, day))
			setDialogOpen(true)
		}
	}

	const selectedEvents = selectedDate
		? eventsByDate.get(toDateKey(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())) || []
		: []

	return (
		<>
			{/* Desktop grid */}
			<div className="hidden md:block">
				{/* Day headers */}
				<div className="grid grid-cols-7 mb-1">
					{DAY_NAMES.map((name) => (
						<div
							key={name}
							className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide py-2"
						>
							{name}
						</div>
					))}
				</div>

				{/* Day cells */}
				<div className="grid grid-cols-7 border-t border-l border-sky-900/10 dark:border-sky-100/10">
					{Array.from({ length: totalCells }, (_, i) => {
						const day = i - firstDayOfWeek + 1
						const isValidDay = day >= 1 && day <= daysInMonth
						const key = isValidDay ? toDateKey(year, month, day) : ""
						const events = isValidDay ? eventsByDate.get(key) || [] : []
						const isToday = isCurrentMonth && day === todayDate

						const paymentEvents = events.filter((e) => e.date_type === "payment")
						const exDateEvents = events.filter((e) => e.date_type === "ex-date")
						const earningsEvents = events.filter((e) => e.event_type === "earnings")

						return (
							<div
								key={i}
								className={cn(
									"border-b border-r border-sky-900/10 dark:border-sky-100/10 min-h-[90px] p-1.5 transition-colors",
									isValidDay
										? "bg-white/30 dark:bg-black/10 hover:bg-sky-50/50 dark:hover:bg-sky-900/10"
										: "bg-gray-50/50 dark:bg-black/5",
									events.length > 0 && "cursor-pointer",
								)}
								onClick={() => isValidDay && handleDayClick(day)}
							>
								{isValidDay && (
									<>
										<div
											className={cn(
												"text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full",
												isToday ? "bg-sky-500 text-white font-bold" : "text-muted-foreground",
											)}
										>
											{day}
										</div>
										<div className="space-y-0.5">
											{paymentEvents.slice(0, 2).map((event, idx) => (
												<div
													key={`pay-${event.ticker}-${idx}`}
													className={cn(
														"text-[10px] leading-tight px-1 py-0.5 rounded truncate font-medium",
														event.status === "paid" &&
															"bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
														event.status === "declared" &&
															"bg-[#0ea5e9]/10 text-[#0ea5e9] dark:text-sky-400",
														event.status === "predicted" &&
															"bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200",
													)}
												>
													{event.ticker}{" "}
													{format(event.amount, { fromCurrency: event.currency })}
												</div>
											))}
											{earningsEvents.map((event, idx) => (
												<div
													key={`earn-${event.ticker}-${idx}`}
													className="text-[10px] leading-tight px-1 py-0.5 rounded font-medium bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 flex items-center gap-0.5"
												>
													<span>{event.ticker}</span>
													{event.earnings_time === "amc" && (
														<Moon className="h-2.5 w-2.5 shrink-0" />
													)}
													{event.earnings_time === "bmo" && (
														<Sun className="h-2.5 w-2.5 shrink-0" />
													)}
												</div>
											))}
											{exDateEvents.slice(0, 1).map((event, idx) => (
												<div
													key={`ex-${event.ticker}-${idx}`}
													className="text-[10px] leading-tight px-1 py-0.5 rounded truncate font-medium border border-dashed border-sky-600/50 text-sky-700 dark:text-sky-300"
												>
													Ex: {event.ticker}
												</div>
											))}
											{(() => {
												const overflow =
													Math.max(0, paymentEvents.length - 2) +
													Math.max(0, exDateEvents.length - 1)
												return overflow > 0 ? (
													<div className="text-[10px] text-muted-foreground font-medium px-1">
														+{overflow} more
													</div>
												) : null
											})()}
										</div>
									</>
								)}
							</div>
						)
					})}
				</div>
			</div>

			{/* Mobile list view */}
			<div className="md:hidden space-y-2">
				{Array.from({ length: daysInMonth }, (_, i) => {
					const day = i + 1
					const key = toDateKey(year, month, day)
					const events = eventsByDate.get(key) || []
					if (events.length === 0) return null

					const isToday = isCurrentMonth && day === todayDate
					const date = new Date(year, month, day)
					const dayLabel = date.toLocaleDateString("en-US", {
						weekday: "short",
						month: "short",
						day: "numeric",
					})
					const paymentEvents = events.filter((e) => e.date_type === "payment")
					const dayTotal = paymentEvents.reduce((sum, e) => sum + convert(e.amount, e.currency), 0)

					return (
						<div
							key={day}
							className={cn(
								"rounded-lg border border-sky-900/10 dark:border-sky-100/10 bg-white/60 dark:bg-black/20 p-3 cursor-pointer hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-colors",
								isToday && "ring-2 ring-sky-500/50",
							)}
							onClick={() => handleDayClick(day)}
						>
							<div className="flex items-center justify-between mb-2">
								<div className="flex items-center gap-2">
									{isToday && <div className="w-2 h-2 rounded-full bg-sky-500" />}
									<span className="text-sm font-semibold text-sky-950 dark:text-sky-50">
										{dayLabel}
									</span>
								</div>
								<span className="text-sm font-bold text-sky-600 dark:text-sky-400">
									{dayTotal > 0 ? format(dayTotal, { skipConversion: true }) : ""}
								</span>
							</div>
							<div className="flex flex-wrap gap-1.5">
								{events.map((event, idx) => (
									<div
										key={`${event.date_type}-${event.ticker}-${idx}`}
										className="flex items-center gap-1"
									>
										{event.event_type === "earnings" ? (
											<div className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT.earnings)} />
										) : event.date_type === "ex-date" ? (
											<div className="w-1.5 h-1.5 rounded-sm border border-dashed border-sky-600" />
										) : (
											<div
												className={cn(
													"w-1.5 h-1.5 rounded-full",
													STATUS_DOT[event.status] || STATUS_DOT.predicted,
												)}
											/>
										)}
										<span className="text-xs text-muted-foreground">
											{event.event_type === "earnings"
												? `${event.ticker} Earnings`
												: event.date_type === "ex-date"
													? `Ex: ${event.ticker}`
													: event.ticker}
										</span>
									</div>
								))}
							</div>
						</div>
					)
				})}
			</div>

			<DayDetailDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				date={selectedDate}
				events={selectedEvents}
			/>
		</>
	)
}
